/// TypeScript types for AI chat, conversations, models, and configuration.
/// Used by the AI store, OpenRouter client, and AI UI components.

// ---------------------------------------------------------------------------
// Chat messages and conversations
// ---------------------------------------------------------------------------

/** Role of a participant in the AI conversation. */
export type ChatRole = "system" | "user" | "assistant";

/** A single message in an AI conversation. */
export interface ChatMessage {
  role: ChatRole;
  content: string;
  timestamp: number;
  /** Which model generated this message (for assistant messages). */
  model?: string;
  /** Whether this message represents an error (API failure, etc). */
  isError?: boolean;
  /** Whether this message is an auto-diagnosis response. */
  isDiagnosis?: boolean;
  /** The tab ID this diagnosis is associated with. */
  diagnosisTabId?: string;
}

/** A conversation tied to a specific editor tab. */
export interface Conversation {
  id: string;
  /** Links this conversation to an editor tab. */
  tabId: string;
  messages: ChatMessage[];
  createdAt: number;
  updatedAt: number;
}

// ---------------------------------------------------------------------------
// OpenRouter model metadata
// ---------------------------------------------------------------------------

/** Model pricing information from OpenRouter API. */
export interface ModelPricing {
  prompt: string;
  completion: string;
}

/** Model architecture information from OpenRouter API. */
export interface ModelArchitecture {
  tokenizer: string;
  modality: string;
}

/** An LLM model available through OpenRouter. */
export interface OpenRouterModel {
  id: string;
  name: string;
  description: string;
  pricing: ModelPricing;
  context_length: number;
  architecture: ModelArchitecture;
}

// ---------------------------------------------------------------------------
// Diagnosis and generation
// ---------------------------------------------------------------------------

/** Diagram type discriminator. */
export type DiagramType = "bpmn" | "nodeflow";

/** Request payload for AI-powered error diagnosis. */
export interface DiagnosisRequest {
  tabId: string;
  errorType: string;
  errorMessage: string;
  elementId: string;
  diagramContent: string;
  diagramType: DiagramType;
}

/** Result of an AI diagram generation. */
export interface GenerationResult {
  type: DiagramType;
  content: string;
  rawResponse: string;
}

// ---------------------------------------------------------------------------
// Error handling
// ---------------------------------------------------------------------------

/** Error from the OpenRouter API with status code and error code. */
export class OpenRouterError extends Error {
  readonly status: number;
  readonly code: string;
  readonly rawBody: unknown;

  constructor(status: number, code: string, rawBody: unknown) {
    super(`OpenRouter API error ${status}: ${code}`);
    this.name = "OpenRouterError";
    this.status = status;
    this.code = code;
    this.rawBody = rawBody;
  }
}
